import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Alert,
  Button,
  Field,
  IconBtn,
  Input,
  Spinner,
} from "../../design-system";
import { AuthShell } from "../../design-system/layout/AuthShell";
import { registerUser, resetRegisterFlag } from "../../slices/thunks";

const pwdRules = Yup.string()
  .min(8, "Mínimo 8 caracteres")
  .matches(/[a-z]/, "Al menos una minúscula")
  .matches(/[A-Z]/, "Al menos una mayúscula")
  .matches(/[0-9]/, "Al menos un número")
  .required("Introduce tu contraseña");

const selectState = createSelector(
  (state) => state.Account,
  (account) => ({
    loading: account.loading,
    success: account.success,
    error: account.registrationError,
    user: account.user,
  }),
);

const Register = () => {
  document.title = "Registro | Kronaly";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const { loading, success, error, user } = useSelector(selectState);
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get("email") || "";
  const nextPath = searchParams.get("next");

  // Persiste el next para que sobreviva al paso por /verify-registration.
  // Lo lee también /verify-registration (sessionStorage) y se borra al usarlo.
  useEffect(() => {
    if (nextPath && nextPath.startsWith("/") && !nextPath.startsWith("//")) {
      sessionStorage.setItem("kronaly_post_auth_next", nextPath);
    }
  }, [nextPath]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: prefilledEmail,
      name: "",
      password: "",
      password_confirmation: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Correo no válido").required("Correo obligatorio"),
      name: Yup.string().required("Nombre completo obligatorio").max(255),
      password: pwdRules,
      password_confirmation: Yup.string()
        .oneOf([Yup.ref("password")], "Las contraseñas no coinciden")
        .required("Confirma tu contraseña"),
    }),
    onSubmit: (values) => {
      dispatch(registerUser(values));
    },
  });

  useEffect(() => {
    dispatch(resetRegisterFlag());
  }, [dispatch]);

  useEffect(() => {
    if (success && user?.email) {
      navigate("/verify-registration", { state: { email: user.email } });
      const t = setTimeout(() => dispatch(resetRegisterFlag()), 500);
      return () => clearTimeout(t);
    }
  }, [success, user, navigate, dispatch]);

  const fieldError = (k) =>
    validation.touched[k] && validation.errors[k] ? validation.errors[k] : null;

  return (
    <AuthShell
      footer={
        <span>
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" style={{ color: "var(--kr-fg-link)", fontWeight: 600 }}>
            Iniciar sesión
          </Link>
        </span>
      }
    >
      <div className="kr-mb-4">
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--kr-fg-strong)", letterSpacing: "-0.01em" }}>
          Crea tu cuenta
        </h1>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--kr-fg-muted)" }}>
          Después confirmarás el correo y registrarás tu empresa.
        </p>
      </div>

      {error && (
        <div className="kr-mb-3">
          <Alert tone="danger">{error}</Alert>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
        }}
        className="kr-flex kr-col kr-gap-4"
        noValidate
      >
        <Field label="Correo electrónico" required error={fieldError("email")}>
          <Input
            type="email"
            name="email"
            value={validation.values.email}
            onChange={validation.handleChange}
            placeholder="empleado@empresa.com"
            autoFocus
            inputProps={{ onBlur: validation.handleBlur, autoComplete: "email" }}
          />
        </Field>

        <Field label="Nombre completo" required error={fieldError("name")}>
          <Input
            type="text"
            name="name"
            value={validation.values.name}
            onChange={validation.handleChange}
            placeholder="Tu nombre completo"
            inputProps={{ onBlur: validation.handleBlur, autoComplete: "name" }}
          />
        </Field>

        <div>
          <label htmlFor="password-input" style={{ fontSize: 12, fontWeight: 500, color: "var(--kr-fg-strong)", display: "block", marginBottom: 6 }}>
            Contraseña <span style={{ color: "var(--kr-danger-500)" }}>*</span>
          </label>
          <div className="kr-relative">
            <Input
              type={showPassword ? "text" : "password"}
              name="password"
              id="password-input"
              value={validation.values.password}
              onChange={validation.handleChange}
              placeholder="Mínimo 8 caracteres"
              inputProps={{ onBlur: validation.handleBlur, autoComplete: "new-password" }}
            />
            <div style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)" }}>
              <IconBtn
                icon={showPassword ? "EyeOff" : "Eye"}
                size={28}
                onClick={() => setShowPassword((v) => !v)}
              />
            </div>
          </div>
          {fieldError("password") && (
            <div className="kr-mt-1 kr-fs-11" style={{ color: "var(--kr-danger-700)" }}>
              {fieldError("password")}
            </div>
          )}
        </div>

        <Field label="Confirma la contraseña" required error={fieldError("password_confirmation")}>
          <Input
            type={showPassword ? "text" : "password"}
            name="password_confirmation"
            value={validation.values.password_confirmation}
            onChange={validation.handleChange}
            placeholder="Repite la contraseña"
            inputProps={{ onBlur: validation.handleBlur, autoComplete: "new-password" }}
          />
        </Field>

        <Button variant="primary" size="lg" type="submit" disabled={loading} fullWidth>
          {loading && <Spinner size={14} color="#fff" />}
          {loading ? "Creando cuenta…" : "Crear cuenta"}
        </Button>
      </form>
    </AuthShell>
  );
};

export default Register;
